import * as THREE from 'three';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { applyMaskChecker } from '@/modules/maskMaterial/maskCheckerMaterial';
import { themeColour } from '@/modules/theme';
import { poseEuler, type OrientationPose } from '../utils/orientation';
import type { BehaviourKey } from '../editorShell/BehaviourTiles';

export const BEHAVIOURS: BehaviourKey[] = ['billboard', 'mask', 'humanMovement', 'shadowCatcher', 'movable'];

const BILLBOARD_DIR = new THREE.Vector3(0, 0.28, 1).normalize();
const EDGE_TRIANGLE_BUDGET = 4000;
const THUMB_TEXTURE_SIZE = 256;
const TEXTURE_SLOTS = ['map', 'emissiveMap', 'aoMap', 'normalMap', 'roughnessMap', 'metalnessMap'] as const;

function own<T extends THREE.Material>(material: T): T {
  material.userData.thumbOwned = true;
  return material;
}

function downscaleTexture(source: THREE.Texture, cache: Map<unknown, THREE.Texture>): THREE.Texture {
  const image = source.image as (CanvasImageSource & { width?: number; height?: number }) | undefined;
  const width = image?.width ?? 0;
  const height = image?.height ?? 0;
  if (!image || !width || !height) return source;
  if (Math.max(width, height) <= THUMB_TEXTURE_SIZE) return source;

  const cached = cache.get(image);
  if (cached) return cached;

  const scale = THUMB_TEXTURE_SIZE / Math.max(width, height);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) return source;
  try {
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  } catch {
    return source;
  }

  const small = new THREE.CanvasTexture(canvas);
  small.colorSpace = source.colorSpace;
  small.wrapS = source.wrapS;
  small.wrapT = source.wrapT;
  small.flipY = source.flipY;
  small.offset.copy(source.offset);
  small.repeat.copy(source.repeat);
  small.center.copy(source.center);
  small.rotation = source.rotation;
  small.needsUpdate = true;
  cache.set(image, small);
  return small;
}

function shrinkTextures(root: THREE.Object3D): THREE.Texture[] {
  const cache = new Map<unknown, THREE.Texture>();
  const swapped = new Map<THREE.Material, THREE.Material>();
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || !mesh.material) return;
    const list = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const next = list.map((original) => {
      const already = swapped.get(original);
      if (already) return already;
      const source = original as unknown as Record<string, THREE.Texture | null>;
      const oversized = TEXTURE_SLOTS.some((slot) => {
        const texture = source[slot];
        const image = texture?.isTexture ? (texture.image as { width?: number; height?: number } | undefined) : undefined;
        return !!image && Math.max(image.width ?? 0, image.height ?? 0) > THUMB_TEXTURE_SIZE;
      });
      if (!oversized) {
        swapped.set(original, original);
        return original;
      }
      const clone = own(original.clone()) as THREE.Material & Record<string, unknown>;
      clone.onBeforeCompile = original.onBeforeCompile;
      clone.customProgramCacheKey = original.customProgramCacheKey;
      clone.needsUpdate = true;
      for (const slot of TEXTURE_SLOTS) {
        const texture = source[slot];
        if (texture?.isTexture) clone[slot] = downscaleTexture(texture, cache);
      }
      swapped.set(original, clone);
      return clone;
    });
    mesh.material = Array.isArray(mesh.material) ? next : next[0];
  });
  return Array.from(cache.values());
}

function facingArrow(radius: number, y: number, z: number): THREE.Object3D {
  const halfShaft = radius * 0.13;
  const shaftLength = radius * 0.95;
  const halfHead = radius * 0.32;
  const headLength = radius * 0.5;

  const shape = new THREE.Shape();
  shape.moveTo(-halfShaft, 0);
  shape.lineTo(halfShaft, 0);
  shape.lineTo(halfShaft, shaftLength);
  shape.lineTo(halfHead, shaftLength);
  shape.lineTo(0, shaftLength + headLength);
  shape.lineTo(-halfHead, shaftLength);
  shape.lineTo(-halfShaft, shaftLength);
  shape.closePath();

  const arrow = new THREE.Mesh(
    new THREE.ShapeGeometry(shape),
    own(new THREE.MeshBasicMaterial({ color: themeColour('lureo-brand'), side: THREE.DoubleSide })),
  );
  arrow.rotation.x = Math.PI / 2;
  arrow.position.set(0, y + radius * 0.004, z);
  arrow.userData.ownsGeometry = true;
  return arrow;
}

function groundDoubleArrow(radius: number, span: number): THREE.Object3D {
  const halfShaft = radius * 0.1;
  const halfHead = radius * 0.26;
  const headLength = radius * 0.42;
  const half = span / 2;

  const shape = new THREE.Shape();
  shape.moveTo(-half, 0);
  shape.lineTo(-half + headLength, halfHead);
  shape.lineTo(-half + headLength, halfShaft);
  shape.lineTo(half - headLength, halfShaft);
  shape.lineTo(half - headLength, halfHead);
  shape.lineTo(half, 0);
  shape.lineTo(half - headLength, -halfHead);
  shape.lineTo(half - headLength, -halfShaft);
  shape.lineTo(-half + headLength, -halfShaft);
  shape.lineTo(-half + headLength, -halfHead);
  shape.closePath();

  const arrow = new THREE.Mesh(
    new THREE.ShapeGeometry(shape),
    own(new THREE.MeshBasicMaterial({ color: themeColour('lureo-brand'), side: THREE.DoubleSide })),
  );
  arrow.rotation.x = Math.PI / 2;
  arrow.userData.ownsGeometry = true;
  return arrow;
}

function applyPose(root: THREE.Object3D, pose: OrientationPose): void {
  const [rx, ry, rz] = poseEuler(pose);
  root.rotation.set(rx, ry, rz);
}

function triangleCount(root: THREE.Object3D): number {
  let total = 0;
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    const pos = mesh.isMesh ? mesh.geometry?.attributes?.position : undefined;
    if (pos) total += (mesh.geometry.index?.count ?? pos.count) / 3;
  });
  return total;
}

function addEdges(root: THREE.Object3D, color: THREE.ColorRepresentation, dashed = false, dashSize = 0.02): void {
  if (triangleCount(root) > EDGE_TRIANGLE_BUDGET) return;
  const targets: THREE.Mesh[] = [];
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh && mesh.geometry) targets.push(mesh);
  });
  for (const mesh of targets) {
    const edges = new THREE.LineSegments(
      new THREE.EdgesGeometry(mesh.geometry, 18),
      dashed
        ? own(new THREE.LineDashedMaterial({ color, dashSize, gapSize: dashSize * 0.85, depthTest: false }))
        : own(new THREE.LineBasicMaterial({ color })),
    );
    if (dashed) {
      edges.computeLineDistances();
      edges.renderOrder = 10;
    }
    edges.userData.ownsGeometry = true;
    mesh.add(edges);
  }
}

function clearBillboardYaw(root: THREE.Object3D): void {
  root.traverse((o) => {
    if ((o.userData as { isBillboardYaw?: boolean })?.isBillboardYaw) o.rotation.set(0, 0, 0);
  });
}

function showAll(root: THREE.Object3D): void {
  root.visible = true;
  root.traverse((o) => { o.visible = true; });
}

function hideHandles(root: THREE.Object3D): void {
  root.traverse((o) => {
    if ((o.userData as { isFFDHandle?: boolean })?.isFFDHandle) o.visible = false;
  });
}

function shadeMaterial(color: THREE.ColorRepresentation, opacity: number, toLight: THREE.Vector3, clip: THREE.Plane[]): THREE.ShaderMaterial {
  return own(new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: new THREE.Color(color) },
      uOpacity: { value: opacity },
      uToLight: { value: toLight.clone() },
    },
    vertexShader: `
      varying vec3 vWorldNormal;
      #include <clipping_planes_pars_vertex>
      void main() {
        #include <begin_vertex>
        #include <project_vertex>
        vWorldNormal = normalize(mat3(modelMatrix) * normal);
        #include <clipping_planes_vertex>
      }
    `,
    fragmentShader: `
      uniform vec3 uColor;
      uniform float uOpacity;
      uniform vec3 uToLight;
      varying vec3 vWorldNormal;
      #include <clipping_planes_pars_fragment>
      void main() {
        #include <clipping_planes_fragment>
        vec3 n = normalize(vWorldNormal);
        if (!gl_FrontFacing) n = -n;
        if (dot(n, uToLight) <= 0.0) discard;
        gl_FragColor = vec4(uColor, uOpacity);
      }
    `,
    side: THREE.DoubleSide,
    transparent: true,
    depthWrite: false,
    clipping: true,
    clippingPlanes: clip,
  }));
}

export interface BehaviourView {
  scene: THREE.Scene;
  target: THREE.Vector3;
  fixedDir: THREE.Vector3 | null;
  relight?: (viewDir: THREE.Vector3) => void;
}

export interface BehaviourScenes {
  views: Partial<Record<BehaviourKey, BehaviourView>>;
  camera: THREE.PerspectiveCamera;
  dist: number;
  dispose: () => void;
}

function shadowCatcherView(src: THREE.Object3D, radius: number, pose: OrientationPose, posedBox: THREE.Box3): BehaviourView {
  const scene = new THREE.Scene();

  const ghost = skeletonClone(src);
  showAll(ghost);
  hideHandles(ghost);
  clearBillboardYaw(ghost);
  applyPose(ghost, pose);
  ghost.updateMatrixWorld(true);
  const box = posedBox;
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());

  const toLight = new THREE.Vector3(0, 0, 1);
  const slab = [new THREE.Plane(new THREE.Vector3(1, 0, 0), 0), new THREE.Plane(new THREE.Vector3(-1, 0, 0), 0)];
  const groundClip = [...slab, new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)];

  const shaded: THREE.ShaderMaterial[] = [];
  const ghostMeshes: THREE.Mesh[] = [];
  ghost.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh && mesh.geometry) ghostMeshes.push(mesh);
  });
  for (const mesh of ghostMeshes) {
    mesh.material = own(new THREE.MeshBasicMaterial({
      colorWrite: false,
      side: THREE.DoubleSide,
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 1,
    }));
    mesh.renderOrder = -1;

    const material = shadeMaterial(themeColour('lureo-tile-shade'), 0.72, toLight, slab);
    shaded.push(material);
    const overlay = new THREE.Mesh(mesh.geometry, material);
    overlay.renderOrder = 2;
    mesh.add(overlay);
  }
  addEdges(ghost, themeColour('lureo-ink-subtle'), true, radius * 0.1);
  scene.add(ghost);

  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(radius * 8, radius * 8),
    own(new THREE.MeshBasicMaterial({
      color: themeColour('lureo-tile-shade'),
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.34,
      depthWrite: false,
      clippingPlanes: groundClip,
    })),
  );
  ground.rotation.x = -Math.PI / 2;
  ground.position.set(center.x, box.min.y - radius * 0.01, center.z);
  ground.renderOrder = 1;
  ground.userData.ownsGeometry = true;
  scene.add(ground);

  scene.add(new THREE.AmbientLight(0xffffff, 1.5));

  const up = new THREE.Vector3(0, 1, 0);
  const camXZ = new THREE.Vector3();
  const side = new THREE.Vector3();
  const relight = (viewDir: THREE.Vector3) => {
    camXZ.set(viewDir.x, 0, viewDir.z);
    if (camXZ.lengthSq() < 1e-8) camXZ.set(0, 0, 1);
    camXZ.normalize();
    toLight.copy(camXZ).applyAxisAngle(up, Math.PI / 3.2);

    side.crossVectors(toLight, up).normalize();
    const spanAcross = Math.abs(side.x) * size.x + Math.abs(side.y) * size.y + Math.abs(side.z) * size.z;
    const halfWidth = Math.max(radius * 0.12, spanAcross * 0.18);
    const sideAtCentre = side.dot(center);

    slab[0].normal.copy(side).negate();
    slab[0].constant = sideAtCentre + halfWidth;
    slab[1].normal.copy(side);
    slab[1].constant = halfWidth - sideAtCentre;
    groundClip[2].normal.copy(toLight);
    groundClip[2].constant = -toLight.dot(center);

    for (const material of shaded) (material.uniforms.uToLight.value as THREE.Vector3).copy(toLight);
  };
  relight(new THREE.Vector3(0, 0, 1));

  return { scene, target: center, fixedDir: null, relight };
}

export function buildBehaviourScenes(
  src: THREE.Object3D,
  pose: OrientationPose,
  billboarded: boolean,
): BehaviourScenes {
  const model = skeletonClone(src);
  showAll(model);
  model.rotation.set(0, 0, 0);
  clearBillboardYaw(model);
  model.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(model);
  const center = box.getCenter(new THREE.Vector3());
  const radius = Math.max(0.001, box.getBoundingSphere(new THREE.Sphere()).radius);
  const extent = box.getSize(new THREE.Vector3());
  const footprint = Math.max(extent.x, extent.z) * 0.5;

  const arrowProbe = facingArrow(footprint, box.min.y, box.max.z + footprint * 0.12);
  arrowProbe.updateMatrixWorld(true);
  const framedRadius = Math.max(
    radius,
    new THREE.Box3().setFromObject(arrowProbe).union(box).getBoundingSphere(new THREE.Sphere()).radius,
  );

  const camera = new THREE.PerspectiveCamera(32, 1, 0.001, 2000);
  const dist = (framedRadius / Math.sin((camera.fov * Math.PI) / 360)) * 1.12;
  camera.near = Math.max(0.001, dist - radius * 4);
  camera.far = dist + radius * 8;
  camera.updateProjectionMatrix();

  hideHandles(model);
  applyPose(model, pose);
  model.updateMatrixWorld(true);
  const posedBox = new THREE.Box3().setFromObject(model);
  const posedCenter = posedBox.getCenter(new THREE.Vector3());

  const shrunk = shrinkTextures(model);

  const billboardScene = new THREE.Scene();
  billboardScene.add(new THREE.AmbientLight(0xffffff, 3.0));
  billboardScene.add(model);
  billboardScene.add(facingArrow(footprint, box.min.y, box.max.z + footprint * 0.12));

  const maskModel = skeletonClone(model);
  showAll(maskModel);
  const checker = own(new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.DoubleSide }));
  applyMaskChecker(checker, radius * 0.55, new THREE.Color(themeColour('lureo-checker-light')), new THREE.Color(themeColour('lureo-checker-dark')));
  maskModel.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh) mesh.material = checker;
  });
  addEdges(maskModel, themeColour('lureo-grey-strong'));
  const maskScene = new THREE.Scene();
  maskScene.add(maskModel);

  const catcherView = shadowCatcherView(src, radius, pose, posedBox);

  // A cross of double-headed arrows on the ground under the asset: free to be
  // dragged anywhere on the plane, which is what the grab rig actually allows.
  const movableModel = skeletonClone(model);
  showAll(movableModel);
  const movableScene = new THREE.Scene();
  movableScene.add(new THREE.AmbientLight(0xffffff, 3.0));
  movableScene.add(movableModel);
  // A near-flat asset has almost no footprint, so the arrows are sized off the
  // bounding radius too or they collapse to nothing
  const arrowScale = Math.max(footprint, radius * 0.35);
  const groundY = posedBox.min.y + radius * 0.004;
  for (const turn of [0, Math.PI / 2]) {
    // The arrow is built lying in the XZ plane, so the in-plane turn goes on a
    // wrapping group rather than fighting the mesh's own euler
    const pivot = new THREE.Group();
    pivot.add(groundDoubleArrow(arrowScale, arrowScale * 2.2));
    pivot.rotation.y = turn;
    pivot.position.set(posedCenter.x, groundY, posedCenter.z);
    movableScene.add(pivot);
  }

  const humanModel = skeletonClone(model);
  showAll(humanModel);
  const humanScene = new THREE.Scene();
  humanScene.add(new THREE.AmbientLight(0xffffff, 3.0));
  humanScene.add(humanModel);

  const views: Partial<Record<BehaviourKey, BehaviourView>> = {
    billboard: { scene: billboardScene, target: center, fixedDir: BILLBOARD_DIR },
    mask: { scene: maskScene, target: posedCenter, fixedDir: billboarded ? BILLBOARD_DIR : null },
    humanMovement: { scene: humanScene, target: posedCenter, fixedDir: billboarded ? BILLBOARD_DIR : null },
    shadowCatcher: billboarded ? { ...catcherView, fixedDir: BILLBOARD_DIR } : catcherView,
    movable: { scene: movableScene, target: posedCenter, fixedDir: billboarded ? BILLBOARD_DIR : null },
  };

  const dispose = () => {
    for (const view of Object.values(views)) {
      view.scene.traverse((o) => {
        const holder = o as unknown as { material?: THREE.Material | THREE.Material[]; geometry?: THREE.BufferGeometry };
        const material = holder.material;
        const list = Array.isArray(material) ? material : material ? [material] : [];
        for (const m of list) if (m.userData?.thumbOwned) m.dispose();
        if ((o.userData as { ownsGeometry?: boolean }).ownsGeometry) holder.geometry?.dispose();
      });
      view.scene.clear();
    }
    for (const texture of shrunk) texture.dispose();
  };

  return { views, camera, dist, dispose };
}
